import { Component, inject, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule, AsyncPipe } from '@angular/common';
import { Auth, signOut, user } from '@angular/fire/auth';
import { Router, RouterModule, NavigationEnd, Event } from '@angular/router';
import { AccessService, PagePermission, Role } from '../services/access.service';
import { switchMap, of, filter, Subscription, combineLatest } from 'rxjs';
import { BrandService } from '../services/brand.service';
import { Brand } from '../models/brand.model';
import { AgentChatComponent } from '../agent-chat/agent-chat.component';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, RouterModule, AsyncPipe, AgentChatComponent],
  templateUrl: './home.component.html',
  styleUrl: './home.component.css'
})
export class HomeComponent implements OnInit, OnDestroy {
  private auth = inject(Auth);
  private router = inject(Router);
  private accessService = inject(AccessService);
  private brandService = inject(BrandService);
  private routerSubscription?: Subscription;
  private brandSubscription?: Subscription;

  user$ = user(this.auth);
  userProfile$ = this.user$.pipe(
    switchMap(user => user ? this.accessService.getUserProfile(user.uid) : of(null))
  );
  pagePermissions$ = this.accessService.getPagePermissions();
  roles$ = this.accessService.getRoles();
  isSidebarOpen = true;
  isLoading = false;
  activeSection = 'MAIN';
  brands: Brand[] = [];
  selectedBrand: Brand | null = null;
  isMobile = false;
  hideBrandSelector = false;

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.isMobile = typeof window !== 'undefined' && window.innerWidth <= 1024;
    if (!this.isMobile && !this.isSidebarOpen) {
      this.isSidebarOpen = true;
    } else if (this.isMobile && this.isSidebarOpen) {
      this.isSidebarOpen = false;
    }
  }

  ngOnInit() {
    this.isMobile = typeof window !== 'undefined' && window.innerWidth <= 1024;
    if (this.isMobile) {
      this.isSidebarOpen = false;
    }

    this.routerSubscription = this.router.events.pipe(
      filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.updateActiveSection(event.urlAfterRedirects);
      // Close sidebar on mobile after navigation
      if (typeof window !== 'undefined' && window.innerWidth <= 1024) {
        this.isSidebarOpen = false;
      }
    });
    this.updateActiveSection(this.router.url);

    this.brandSubscription = combineLatest([
      this.brandService.getBrands(),
      this.userProfile$,
      this.roles$
    ]).subscribe(async ([allBrands, profile, roles]) => {
      if (!allBrands || !allBrands.length) {
        this.brands = [];
        return;
      }

      const resolvedRole = this.getResolvedRole(profile, roles);
      if (resolvedRole?.isExternal && profile?.ownerIds && profile.ownerIds.length > 0) {
        // External Role: Filter brands strictly to owner's company brands
        const allowedBrandIds = await this.accessService.getUserOwnerBrandIds(profile.ownerIds);
        this.brands = allBrands.filter(b => 
          allowedBrandIds.some((ab: string) => 
            (b.id && ab.toLowerCase().trim() === b.id.toLowerCase().trim()) || 
            (b.name && ab.toLowerCase().trim() === b.name.toLowerCase().trim())
          )
        );
      } else if (resolvedRole?.isExternal && (!profile?.ownerIds || profile.ownerIds.length === 0)) {
        // External role with no owner assigned -> no brands available
        this.brands = [];
      } else {
        // Regular role / Superadmin -> all brands
        this.brands = allBrands;
      }

      if (this.brands.length > 0) {
        const savedBrandId = this.brandService.getSelectedBrandIdFromStorage();
        const savedBrand = savedBrandId ? this.brands.find(b => b.id === savedBrandId) : null;

        if (savedBrand) {
          this.selectBrand(savedBrand);
        } else {
          this.selectBrand(this.brands[0]);
        }
      } else {
        this.selectedBrand = null;
      }
    });

    this.brandService.selectedBrand$.subscribe(brand => {
      this.selectedBrand = brand;
    });
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.brandSubscription) {
      this.brandSubscription.unsubscribe();
    }
  }

  updateActiveSection(url: string) {
    // The Profit Report spans all brands (KAN-44), so the global brand selector is hidden there.
    this.hideBrandSelector = url.split(/[?#]/)[0].includes('/profit-report');
    if (url.includes('/invoice') || url.includes('/sales-invoice')) {
      this.activeSection = 'INVOICE';
    } else if (url.includes('/profit-report') || url.includes('/daily-report') || url.includes('/smrs-fee-report') || url.includes('/smre-fee-report') || url.includes('/sales-report') || url.includes('/frame-report') || url.includes('/artist-statement')) {
      this.activeSection = 'REPORTS';
    } else if (url.includes('/accounts') || url.includes('/company-management') || url.includes('/owner-management') || url.includes('/store-management') || url.includes('/smrs-rate-management') || url.includes('/bank-management') || url.includes('/dev-tool') || url.includes('/access-control') || url.includes('/agent-management') || url.includes('/artist-management') || url.includes('/coupon-management') || url.includes('/brand-management') || url.includes('/photobooth-management')) {
      this.activeSection = 'ADMIN';
    } else {
      this.activeSection = 'MAIN';
    }
  }

  toggleSidebar() { this.isSidebarOpen = !this.isSidebarOpen; }

  handleLoading(loading: any) {
    this.isLoading = loading;
  }

  logout() {
    signOut(this.auth).then(() => this.router.navigate(['/login'], { queryParams: { logout: 'true' } }));
  }

  getResolvedRole(user: any, roles: Role[] | null | undefined): Role | undefined {
    if (!user || !roles || !roles.length) return undefined;
    const roleStr = typeof user === 'string' ? user : user.role;
    const roleId = typeof user === 'string' ? undefined : (user.roleId || user.role_id);
    return roles.find(r =>
      (r.id && (r.id === roleId || r.id === roleStr)) ||
      (r.name && roleStr && r.name.toLowerCase() === roleStr.toLowerCase())
    );
  }

  getRoleName(user: any, roles: Role[] | null | undefined): string {
    if (!user) return 'Guest';
    const found = this.getResolvedRole(user, roles);
    if (found) return found.name;
    const roleStr = typeof user === 'string' ? user : user.role;
    if (roleStr && !roleStr.startsWith('http') && roleStr.length < 30) {
      return roleStr;
    }
    return 'User';
  }

  canAccessChatAgent(role: string, permissions: PagePermission[] | null): boolean {
    if (!role || !permissions) return false;
    const normalizedRole = role.toLowerCase().trim();
    if (normalizedRole === 'superadmin') return true;

    // 1. Parent 'chat-agent' must be granted to this role
    const parentPerm = permissions.find(p => p.id === 'chat-agent');
    if (!parentPerm || !parentPerm.roles || !parentPerm.roles.some(r => r.toLowerCase() === normalizedRole)) {
      return false;
    }

    // 2. Child agents check: if sub-permissions exist in database, user must have access to at least 1 agent
    const childPerms = permissions.filter(p => p.id && p.id.startsWith('chat-agent_'));
    if (childPerms.length > 0) {
      return childPerms.some(p => p.roles && p.roles.some(r => r.toLowerCase() === normalizedRole));
    }

    return true;
  }

  canAccess(pageId: string, roleOrUser: any, permissions: PagePermission[] | null, roles?: Role[] | null): boolean {
    if (!roleOrUser || !permissions) return false;
    const roleStr = typeof roleOrUser === 'string' ? roleOrUser : this.getRoleName(roleOrUser, roles);
    if (!roleStr) return false;
    const normalizedRole = roleStr.toLowerCase().trim();
    if (normalizedRole === 'superadmin') return true;

    const resolvedRole = typeof roleOrUser === 'object' ? this.getResolvedRole(roleOrUser, roles) : roles?.find(r => r.name.toLowerCase() === normalizedRole);
    if (resolvedRole?.isCustomer) {
      return false;
    }

    if (resolvedRole?.isExternal) {
      if (pageId !== 'profit-report' && pageId !== 'daily-report') {
        return false;
      }
    }

    if (pageId === 'chat-agent') {
      return this.canAccessChatAgent(normalizedRole, permissions);
    }

    const perm = permissions.find(p => p.id === pageId);
    return perm ? perm.roles.some(r => r.toLowerCase() === normalizedRole) : false;
  }

  selectBrand(brand: Brand) {
    this.brandService.setSelectedBrand(brand);
  }
}